/**
 * Which columns this person sees, remembered between visits.
 *
 * Kept in local storage rather than on the server because there is nowhere on the
 * server to put it: no preferences column, no preferences table, no endpoint. Building
 * one is a migration, a schema and a route for a list of ticked boxes.
 * `usePersistentState` exists for exactly this and says so: interface preferences,
 * never server data, never anything sensitive. A column choice is neither.
 *
 * The key carries the user id, because two people sharing a browser sharing one column
 * set is the opposite of "per user".
 */

import { useCallback, useMemo } from 'react';

import { usePersistentState } from '@/lib/usePersistentState';

import {
  COLUMN_KEYS,
  DEFAULT_COLUMN_KEYS,
  LOCKED_COLUMN_KEY,
  type CandidateColumnKey,
} from './columns';

/**
 * The version suffix is an escape hatch.
 *
 * The set of columns will change (an assignee column, a meetings column). The
 * intersection below copes with that, but bumping the version discards every stored
 * choice wholesale if it ever stops being enough.
 */
export function columnStorageKey(userId: string): string {
  return `flyst.candidates.columns.v1.${userId}`;
}

function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((key, index) => key === b[index]);
}

export function useCandidateColumns(userId: string) {
  const [stored, setStored] = usePersistentState<CandidateColumnKey[]>(
    columnStorageKey(userId),
    DEFAULT_COLUMN_KEYS,
  );

  const visible = useMemo<CandidateColumnKey[]>(() => {
    // What is in storage was written by an earlier release. Drop anything that is no
    // longer a column, and put the locked one back if it went missing, so a renamed
    // column degrades to a slightly shorter table rather than to a blank one or a
    // renderer with no case to match.
    const kept = stored.filter((key) => COLUMN_KEYS.has(key));
    const withLock = kept.includes(LOCKED_COLUMN_KEY) ? kept : [LOCKED_COLUMN_KEY, ...kept];
    return withLock.length > 0 ? withLock : DEFAULT_COLUMN_KEYS;
  }, [stored]);

  const toggle = useCallback(
    (key: CandidateColumnKey) => {
      if (key === LOCKED_COLUMN_KEY) return;
      setStored((current) => {
        const kept = current.filter((item) => COLUMN_KEYS.has(item));
        return kept.includes(key) ? kept.filter((item) => item !== key) : [...kept, key];
      });
    },
    [setStored],
  );

  const reset = useCallback(() => {
    setStored(DEFAULT_COLUMN_KEYS);
  }, [setStored]);

  // Compared against the defaults in COLUMNS order, not as sets, so the reset button
  // reads as available whenever the stored order has drifted too.
  const isDefault = useMemo(
    () => sameKeys([...visible].sort(), [...DEFAULT_COLUMN_KEYS].sort()),
    [visible],
  );

  return { visible, toggle, reset, isDefault } as const;
}
